import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  styled,
  InputBase,
  IconButton,
  Modal,
  TextField,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { CELL_PHONE_COBRANZA } from "../../Constants";
import useContactList from "./hooks/useContactList";
import { axiosClient } from "../AxiosClient";

const ContactListContainer = styled(Box)(({ theme }) => ({
  width: "25%",
  height: "calc(100vh - 100px)",
  backgroundColor: "#fff",
  borderRight: "1px solid #e0e0e0",
  position: "fixed",
  left: "240px",
  top: "100px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  padding: "8px 16px",
  backgroundColor: "#f0f2f5",
  borderBottom: "1px solid #e0e0e0",
}));

const ContactsListWrapper = styled(Box)({
  flex: 1,
  overflowY: "auto",
});

const SearchBar = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: "#fff",
  borderRadius: "8px",
  padding: "4px 8px",
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  marginLeft: "8px",
}));

const ContactItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  padding: "12px 16px",
  "&:hover": {
    backgroundColor: "#f0f2f5",
    cursor: "pointer",
  },
  backgroundColor: active ? "#e9f5f1" : "transparent",
  borderLeft: active ? "4px solid #00a884" : "4px solid transparent",
  transition: "background-color 0.2s ease, border-left 0.2s ease",
}));

const LastMessage = styled(Typography)({
  color: "#667781",
  fontSize: "14px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "80%",
  display: "inline-block",
});

const MessageTime = styled(Typography)({
  color: "#667781",
  fontSize: "12px",
  marginLeft: "auto",
  paddingLeft: "8px",
});

const ContactName = styled(Typography)({
  fontWeight: 500,
  fontSize: "16px",
});

const NewChatModal = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  backgroundColor: "#fff",
  borderRadius: "8px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  padding: "24px",
});

const NewChatButton = styled(IconButton)({
  backgroundColor: "#00a884",
  color: "#fff",
  "&:hover": {
    backgroundColor: "#008f72",
  },
});

export default function ContactList({ onContactSelect, selectedContact, updateTrigger }) {
  const [contacts, setContacts] = useState();
  const [searchTerm, setSearchTerm] = useState("");
  const [newChatModal, setNewChatModal] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [filteredContacts, setFilteredContacts] = useState();

  const handleNewChat = () => {
    setNewChatModal(true);
    setNewPhoneNumber("");
    setPhoneError("");
  };

  const handleCloseNewChat = () => {
    setNewChatModal(false);
    setNewPhoneNumber("");
    setPhoneError("");
  };

  const handleContactClick = (contact) => {
    onContactSelect(contact);
  };

  const getContactKey = (contact) => {
    if (!contact) return null;
    if (contact.uuid) return contact.uuid;
    // Si no hay uuid, usar número contraparte (quien no es CELL_PHONE_COBRANZA)
    return contact.senderCellphone === CELL_PHONE_COBRANZA
      ? contact.recipientCellphone
      : contact.senderCellphone;
  };

  const startNewChat = (phone) => {
    const phoneWithPrefix = "57" + phone;
    const tempContact = {
        uuid: `temp-${Date.now()}`,
        recipientCellphone: phoneWithPrefix,
        senderCellphone: CELL_PHONE_COBRANZA,
        cellphone: phoneWithPrefix,
        name: phoneWithPrefix,
        message: "Iniciando conversación...",
        timeSent: new Date().toLocaleTimeString(),
        type: "text",
      };
    return tempContact;
  }

  const handleStartNewChat = () => {
    const phone = newPhoneNumber.trim();
    try {
      const newContact = startNewChat(phone);
      onContactSelect(newContact);
      handleCloseNewChat();
      setContacts([...contacts, newContact]);
      setFilteredContacts([...contacts, newContact]);
    } catch (err) {
      setPhoneError(err.message);
    }
  };

  const searchByCellphone = () => {
    axiosClient.get(`/v1/conversation-lists/cellphone/${CELL_PHONE_COBRANZA}`)
    .then((response) => {
      const contacts = response.data;
      setContacts(contacts);
      setFilteredContacts(contacts);
    })
    .catch((error) => {
      console.error("Error fetching contacts:", error);
    });
  }

  const filterContacts = (search) => {
    const searchLower = search.toLowerCase();
    const filtered = contacts?.filter((contact) => {
      const nameMatch = contact.name
        ? contact.name.toLowerCase().includes(searchLower)
        : false;
      const cellphoneSenderMatch = contact.senderCellphone
        ? contact.senderCellphone.includes(searchTerm)
        : false;
      const cellphoneRecipientMatch = contact.recipientCellphone
        ? contact.recipientCellphone.includes(searchTerm)
        : false;
      return nameMatch || cellphoneSenderMatch || cellphoneRecipientMatch;
    });
    setFilteredContacts(filtered);
  }

  useEffect(() => {
    searchByCellphone();
  }, [selectedContact, updateTrigger]);

  useEffect(() => {
    filterContacts(searchTerm);
  }, [searchTerm, contacts]);

  return (
    <ContactListContainer>
      <SearchContainer>
        <SearchBar>
          <IconButton sx={{ p: "10px" }}>
            <SearchIcon />
          </IconButton>
          <SearchInput
            placeholder="Buscar o empezar un nuevo chat"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <NewChatButton onClick={handleNewChat} size="small">
            <AddIcon />
          </NewChatButton>
        </SearchBar>
      </SearchContainer>

      <ContactsListWrapper>
        <List sx={{ padding: 0 }}>
          {filteredContacts?.map((contact) => (
            <ContactItem
              key={contact.uuid}
              onClick={() => handleContactClick(contact)}
              active={getContactKey(selectedContact) === getContactKey(contact)}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: "#00a884" }}>
                  {(
                    contact.name?.[0] ||
                    contact.lastName?.[0] ||
                    contact.senderCellphone?.[0] ||
                    "?"
                  ).toUpperCase()}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <ContactName component="span">
                    {contact.name || contact.lastName
                      ? `${contact.name}`.trim()
                      : contact.senderCellphone == CELL_PHONE_COBRANZA
                      ? contact.recipientCellphone
                      : contact.senderCellphone || "Desconocido"}
                  </ContactName>
                }
                secondary={
                  <Box
                    component="span"
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <LastMessage component="span">
                      {contact.type === "text"
                        ? contact.message
                        : contact.type || "No hay mensajes"}
                    </LastMessage>
                    <MessageTime component="span">
                      {contact.timeSent}
                    </MessageTime>
                  </Box>
                }
              />
            </ContactItem>
          ))}
        </List>
      </ContactsListWrapper>

      <Modal open={newChatModal} onClose={handleCloseNewChat}>
        <NewChatModal>
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Nueva Conversación
          </Typography>
          <TextField
            fullWidth
            label="Número de teléfono"
            variant="outlined"
            value={newPhoneNumber}
            onChange={(e) => {
              setNewPhoneNumber(e.target.value);
              setPhoneError("");
            }}
            sx={{ marginBottom: 2 }}
            error={!!phoneError}
            helperText={phoneError}
            inputProps={{ maxLength: 10 }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={handleCloseNewChat}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleStartNewChat}
              disabled={
                !newPhoneNumber.trim() ||
                newPhoneNumber.trim().length !== 10 ||
                !/^[0-9]+$/.test(newPhoneNumber.trim())
              }
              sx={{
                backgroundColor: "#00a884",
                "&:hover": {
                  backgroundColor: "#008f72",
                },
              }}
            >
              Iniciar chat
            </Button>
          </Box>
        </NewChatModal>
      </Modal>
    </ContactListContainer>
  );
}
